/**
 * RoadblockRecordModal
 *
 * Read-only detail modal for a single Route Roadblock submission record.
 * Opens when a row is tapped in the Recent Roadblocks table.
 *
 * Features
 * ─────────
 * • Displays all record fields read-only
 * • "Details" section with full untruncated text (includes missing equipment list)
 * • "Requested Time Window" section when the record carries time window data
 * • "Notify my Supervisor" button → NotifySupervisorModal
 * • Close via X button, "Close" footer button, or Escape key (web)
 * • Shows toast and refreshes table when the record no longer exists
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  StyleSheet,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { FontFamily } from '../../constants/fonts';
import {
  fetchRoadblockSubmissionById,
  RecentRoadblockSubmission,
} from '../../api/deliveryRpmApi';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import NotifySupervisorModal from './NotifySupervisorModal';
import TruckPhotoGrid from './components/TruckPhotoGrid';
import { fetchEquipmentConfigItems } from '../PerfectTruck/perfectTruckApi';
import { EquipmentConfigItem } from '../Admin/pages/PerfectTruckManagement/types';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const isRecordGoneError = (err: any) => {
  const status = err?.response?.status;
  if (status === 404 || status === 410) return true;

  const msg = String(
    err?.response?.data?.error ??
    err?.response?.data?.message ??
    err?.message ??
    '',
  ).toLowerCase();

  return (
    msg.includes('not found') ||
    msg.includes('no longer available') ||
    msg.includes('status code 404') ||
    msg.includes('status code 410')
  );
};

const isMissingSubmission = (sub: any) => {
  return !sub || typeof sub.id !== 'number' || Number.isNaN(sub.id);
};

const formatTime = (t: string | null) => {
  if (!t) return '—';
  // Accept "HH:MM" or "HH:MM:SS"
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Expand abbreviated day codes to full names
const DAYS: Record<string, string> = {
  Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday',
  Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday',
};
const formatDays = (raw: string | null) => {
  if (!raw) return '—';
  return raw.split(',').map(d => DAYS[d.trim()] ?? d.trim()).join(', ');
};

// ─── Missing-equipment translation ──────────────────────────────────────────
// `missingEquipment` is stored as a comma-joined free-text string using whatever
// language was active when the record was submitted. Equipment config items have
// a stable `id` across languages, so we build a name↔id map from all supported
// languages once, then re-translate the stored names into the currently viewed
// language for display (falling back to the raw text when no match is found).
type EquipmentMultiLangMap = {
  idToLangName: Record<number, Record<string, string>>;
  nameToId: Record<string, number>;
};

let equipmentMultiLangCache: Promise<EquipmentMultiLangMap> | null = null;

const loadEquipmentMultiLangMap = (): Promise<EquipmentMultiLangMap> => {
  if (!equipmentMultiLangCache) {
    equipmentMultiLangCache = Promise.all([
      fetchEquipmentConfigItems({ language: 'en' }),
      fetchEquipmentConfigItems({ language: 'fr' }),
      fetchEquipmentConfigItems({ language: 'es' }),
    ])
      .then(([en, fr, es]) => {
        const idToLangName: Record<number, Record<string, string>> = {};
        const nameToId: Record<string, number> = {};
        const register = (items: EquipmentConfigItem[], lang: string) => {
          items.forEach((item) => {
            if (!idToLangName[item.id]) idToLangName[item.id] = {};
            idToLangName[item.id][lang] = item.equipmentName;
            const key = item.equipmentName.trim().toLowerCase();
            if (key) nameToId[key] = item.id;
          });
        };
        register(en, 'en');
        register(fr, 'fr');
        register(es, 'es');
        return { idToLangName, nameToId };
      })
      .catch(() => ({ idToLangName: {}, nameToId: {} }));
  }
  return equipmentMultiLangCache;
};

// ─── Image helpers ───────────────────────────────────────────────────────────

interface AttachedImage {
  fileName: string;
  uri: string;
}

const normalizeAttachedImages = (imageResponse: unknown): AttachedImage[] => {
  if (!imageResponse) return [];

  const normalizeFromValue = (value: unknown, fallbackName = 'Attached image'): AttachedImage[] => {
    if (!value) return [];

    // Preferred shape: { [originalFileName]: presignedUrl }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'string' && v.trim().length > 0)
        .map(([k, v], idx) => ({
          fileName: (k || '').trim() || `${fallbackName} ${idx + 1}`,
          uri: (v as string).trim(),
        }));
    }

    // Backward-compatible shape: [url1, url2] or a single url string
    if (Array.isArray(value)) {
      return value
        .filter(v => typeof v === 'string' && v.trim().length > 0)
        .map((v, idx) => ({ fileName: `${fallbackName} ${idx + 1}`, uri: (v as string).trim() }));
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      return [{ fileName: fallbackName, uri: trimmed }];
    }

    return [];
  };

  if (typeof imageResponse === 'string') {
    const raw = imageResponse.trim();
    if (!raw) return [];
    try {
      return normalizeFromValue(JSON.parse(raw));
    } catch {
      return normalizeFromValue(raw);
    }
  }

  return normalizeFromValue(imageResponse);
};

// ─── Field row ────────────────────────────────────────────────────────────────

const FieldRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const ms = useDarkStyles(baseMs);
  return (
    <View style={ms.fieldRow}>
      <Text style={ms.fieldLabel}>{label}</Text>
      <Text style={ms.fieldValue}>{value || '—'}</Text>
    </View>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

interface RoadblockRecordModalProps {
  recordId: string | null;
  onClose: () => void;
  onRecordGone: () => void;
}

const RoadblockRecordModal: React.FC<RoadblockRecordModalProps> = ({
  recordId,
  onClose,
  onRecordGone,
}) => {
  const dc = useDarkColor();
  const ms = useDarkStyles(baseMs);
  const { t, i18n } = useTranslation('harbor');
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const roadblockRcMap = useSelector((state: RootState) => state.deliveryRpm.roadblockRcMap);

  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState<RecentRoadblockSubmission | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [equipmentMap, setEquipmentMap] = useState<EquipmentMultiLangMap | null>(null);

  // Escape key on web
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handler = (e: any) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Load the multi-language equipment name↔id map once (cached across modal opens)
  useEffect(() => {
    let cancelled = false;
    loadEquipmentMultiLangMap().then((map) => { if (!cancelled) setEquipmentMap(map); });
    return () => { cancelled = true; };
  }, []);

  // Load record whenever recordId changes
  useEffect(() => {
    if (!recordId) { setSub(null); return; }
    let cancelled = false;
    setLoading(true);
    setSub(null);

    fetchRoadblockSubmissionById(Number(recordId))
      .then((s) => {
        if (cancelled) return;
        if (isMissingSubmission(s)) {
          onRecordGone();
          return;
        }
        setSub(s);
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (isRecordGoneError(err)) {
          onRecordGone();
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [recordId, onRecordGone]);

  const getRecordTypeLabel = (recordType: string | null): string => {
    if (recordType === 'Stop Issue') {
      return t('route_roadblocks.stop_issue');
    }
    if (recordType === 'Route Issue') {
      return t('route_roadblocks.route_issue');
    }
    return recordType ?? '—';
  };

  const isStopIssue = sub?.recordType === 'Stop Issue';

  // Re-translates a stored comma-joined equipment-name string into the currently
  // active language via the id-based lookup; falls back to the raw text when a
  // name can't be matched (e.g. legacy/unknown equipment).
  const translateMissingEquipment = (raw: string): string => {
    if (!equipmentMap) return raw;
    const lang = i18n.language?.split('-')[0] || 'en';
    return raw
      .split(',')
      .map((token) => {
        const trimmed = token.trim();
        if (!trimmed) return '';
        const id = equipmentMap.nameToId[trimmed.toLowerCase()];
        if (id == null) return trimmed;
        return equipmentMap.idToLangName[id]?.[lang] ?? trimmed;
      })
      .filter(Boolean)
      .join(', ');
  };

  // Build details text — include missingEquipment list if present
  const detailsText = (() => {
    if (!sub) return '';
    const parts: string[] = [];
    if (sub.additionalDetails) parts.push(sub.additionalDetails);
    if (sub.missingEquipment) {
      parts.push(`${t('route_issue_submit.label_missing_equipment')}: ${translateMissingEquipment(sub.missingEquipment)}`);
    }
    return parts.join('\n\n');
  })();

  const hasTimeWindow = !!(sub?.daysOfWeek || sub?.requestedOpenTime || sub?.requestedCloseTime);
  const attachedImages = normalizeAttachedImages(sub?.imageResponse);
  const hasImages = attachedImages.length > 0;

  const visible = !!recordId;

  return (
    <>
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
      >
      <View style={[ms.overlay, isMobile && ms.overlayMobile]}>
          <View style={[ms.sheet, isMobile && ms.sheetMobile]}>
            {/* ── Header ─────────────────────────────────────────────── */}
            <View style={ms.header}>
              <Text style={ms.title}>{t('roadblock_record_modal.title')}</Text>
              <TouchableOpacity style={ms.closeBtn} onPress={onClose} accessibilityLabel="Close">
                <MaterialIcons name="close" size={18} color={dc("#616161")} />
              </TouchableOpacity>
            </View>

            {/* ── Body ───────────────────────────────────────────────── */}
            <ScrollView style={ms.body} contentContainerStyle={ms.bodyContent}>
              {loading && (
                <View style={ms.loadingWrap}>
                  <ActivityIndicator size="large" color={dc("#005CBC")} />
                </View>
              )}

              {!loading && sub && (
                <>
                  {/* Fields */}
                  <View style={ms.section}>
                    <View style={ms.fieldsGrid}>
                      <FieldRow label={t('roadblock_record_modal.label_type')} value={getRecordTypeLabel(sub.recordType)} />
                      <FieldRow label={t('roadblock_record_modal.label_route')} value={sub.localRoute ?? sub.routeId ?? '—'} />
                      {isStopIssue && (
                        <FieldRow label={t('roadblock_record_modal.label_sscc')} value={sub.sscc ?? '—'} />
                      )}
                      <FieldRow label={t('roadblock_record_modal.label_date')} value={formatDate(sub.deliveryDate)} />
                      <FieldRow label={t('roadblock_record_modal.label_reason')} value={roadblockRcMap[sub.reasonCodeId] ?? String(sub.reasonCodeId)} />
                      <FieldRow label={t('roadblock_record_modal.label_driver')} value={sub.driverName ?? sub.driverGpid ?? '—'} />
                    </View>
                  </View>

                  {/* Details */}
                  <View style={ms.section}>
                    <Text style={ms.sectionTitle}>{t('roadblock_record_modal.label_details')}</Text>
                    {detailsText ? (
                      <Text style={ms.detailsText}>{detailsText}</Text>
                    ) : (
                      <Text style={ms.noDetailsText}>{t('roadblock_record_modal.section_submission')}</Text>
                    )}
                  </View>

                  {/* Requested Time Window */}
                  {hasTimeWindow && (
                    <View style={ms.section}>
                      <Text style={ms.sectionTitle}>{t('roadblock_record_modal.section_time_window')}</Text>
                      <View style={ms.fieldsGrid}>
                        <FieldRow label={t('roadblock_record_modal.label_days')} value={formatDays(sub.daysOfWeek)} />
                        <FieldRow label={t('roadblock_record_modal.label_time_from')} value={formatTime(sub.requestedOpenTime)} />
                        <FieldRow label={t('roadblock_record_modal.label_time_to')} value={formatTime(sub.requestedCloseTime)} />
                      </View>
                    </View>
                  )}

                  {/* Images */}
                  {hasImages && (
                    <View style={ms.section}>
                      <Text style={ms.sectionTitle}>{t('roadblock_record_modal.section_photos')}</Text>
                      <TruckPhotoGrid
                        photos={Object.fromEntries(attachedImages.map(img => [img.fileName, img.uri]))}
                      />
                    </View>
                  )}
                </>
              )}
            </ScrollView>

            {/* ── Footer ─────────────────────────────────────────────── */}
            <View style={ms.footer}>
              <TouchableOpacity
                style={ms.notifyBtn}
                onPress={() => setNotifyOpen(true)}
                accessibilityLabel="Notify my Supervisor"
              >
                <MaterialIcons name="notifications" size={16} color={dc("#034895")} style={{ marginRight: 6 }} />
                <Text style={ms.notifyBtnText}>{t('roadblock_record_modal.action_notify_supervisor')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={ms.closeFooterBtn} onPress={onClose} accessibilityLabel="Close modal">
                <Text style={ms.closeFooterBtnText}>{t('roadblock_record_modal.action_close')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Notify supervisor */}
      {notifyOpen && sub && (
        <NotifySupervisorModal
          recordType="Roadblock"
          recordId={sub.id}
          onClose={() => setNotifyOpen(false)}
          onBack={() => setNotifyOpen(false)}
        />
      )}
    </>
  );
};

export default RoadblockRecordModal;

// ─── Modal styles ─────────────────────────────────────────────────────────────

const baseMs = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  sheet: {
    backgroundColor: '#fff',
    borderRadius: 10,
    width: '100%',
    maxWidth: 560,
    maxHeight: '90%',
    overflow: 'hidden',
    ...Platform.select({
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.18, shadowRadius: 24 },
      android: { elevation: 8 },
    }),
  },
  sheetMobile: { flex: 1, borderRadius: 0 },
  overlayMobile: { padding: 0 },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#EBEBEB',
  },
  title: { fontSize: 20, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },
  closeBtn: {
    width: 32, height: 32, borderRadius: 6,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#F5F5F5',
  },

  body: { flex: 1 },
  bodyContent: { padding: 24, gap: 20 },

  loadingWrap: { paddingVertical: 60, alignItems: 'center' },

  section: { gap: 10 },
  sectionTitle: { fontSize: 15, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },

  fieldsGrid: { gap: 10 },
  fieldRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  fieldLabel: {
    width: 110, fontSize: 13, fontWeight: '700', color: '#616161',
    fontFamily: FontFamily.bold, flexShrink: 0,
  },
  fieldValue: { flex: 1, fontSize: 14, color: '#1A1A1A', fontFamily: FontFamily.regular, lineHeight: 20 },

  detailsText: {
    fontSize: 14, color: '#1A1A1A', fontFamily: FontFamily.regular,
    lineHeight: 22, backgroundColor: '#F7F8FA',
    borderRadius: 6, padding: 12,
  },
  noDetailsText: {
    fontSize: 13, color: '#9E9E9E', fontFamily: FontFamily.regular,
    fontStyle: 'italic',
  },

  photosGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  photoCard: { width: 100, gap: 6 },
  photoName: { fontSize: 11, color: '#616161', fontFamily: FontFamily.regular },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: '#EBEBEB',
    flexWrap: 'wrap',
    gap: 10,
  },
  notifyBtn: {
    flexDirection: 'row', alignItems: 'center',
    height: 38, paddingHorizontal: 16, borderRadius: 24,
    borderWidth: 1, borderColor: '#034895', backgroundColor: '#fff',
  },
  notifyBtnText: { fontSize: 14, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  closeFooterBtn: {
    height: 38, paddingHorizontal: 28, borderRadius: 24,
    backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center',
  },
  closeFooterBtnText: { fontSize: 14, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
});
